import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Euro, FileDown, Clock, Users, TrendingUp, Printer } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function fmt(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function startOfWeek(d) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
}
function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

const ROLES = ["", "Cameriere", "Cameriere senior", "Maître", "Sommelier", "Barman", "Chef", "Sous-chef", "Pizzaiolo", "Pasticciere", "Lavapiatti", "Hostess", "Runner"];

export default function StatsPage() {
    const today = useMemo(() => new Date(), []);
    const [preset, setPreset] = useState("week");
    const [start, setStart] = useState(fmt(startOfWeek(today)));
    const [end, setEnd] = useState(fmt(addDays(startOfWeek(today), 6)));
    const [employeeId, setEmployeeId] = useState("");
    const [role, setRole] = useState("");
    const [location, setLocation] = useState("");
    const [f24Rate, setF24Rate] = useState(55); // percentage
    const [empType, setEmpType] = useState("all"); // all | employee | on_call
    const [showCats, setShowCats] = useState({
        hourly: true,
        salary: true,
        extra: true,
        f24: true,
    });
    const [employees, setEmployees] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const loadEmployees = async () => {
        const { data } = await api.get("/employees");
        setEmployees(data.items.filter((i) => i.status === "active"));
    };

    const applyPreset = (p) => {
        setPreset(p);
        const t = new Date();
        if (p === "day") {
            setStart(fmt(t));
            setEnd(fmt(t));
        } else if (p === "week") {
            const ws = startOfWeek(t);
            setStart(fmt(ws));
            setEnd(fmt(addDays(ws, 6)));
        } else if (p === "month") {
            setStart(fmt(startOfMonth(t)));
            setEnd(fmt(endOfMonth(t)));
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = { start, end, f24_rate: f24Rate / 100 };
            if (employeeId) params.employee_id = employeeId;
            if (role) params.role = role;
            if (location) params.location = location;
            const { data } = await api.get("/stats/costs", { params });
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmployees();
    }, []);
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fmtEur = (n) =>
        new Intl.NumberFormat("it-IT", {
            style: "currency",
            currency: "EUR",
        }).format(n || 0);

    const printStatsPDF = async () => {
        try {
            const startStr = start;
            const endStr = end;

            const params = { start: startStr, end: endStr, f24_rate: f24Rate / 100 };
            if (employeeId) params.employee_id = employeeId;
            if (role) params.role = role;
            if (location) params.location = location;
            const { data: m } = await api.get("/stats/costs", { params });

            let onCallCost = 0;
            let onCallHours = 0;
            let employeesSalary = 0;
            let employeesExtra = 0;
            let onCallExtra = 0;
            const onCallRows = [];
            const empRows = [];
            m.per_employee.forEach((p) => {
                if (p.employment_type === "employee") {
                    employeesSalary += p.monthly_cost_in_range || 0;
                    employeesExtra += p.extra_cost || 0;
                    empRows.push(p);
                } else {
                    onCallCost += p.hourly_cost || 0;
                    onCallHours += p.hours || 0;
                    onCallExtra += p.extra_cost || 0;
                    onCallRows.push(p);
                }
            });
            const f24Total = m.totals.simulated_f24 || 0;
            const days = m.range.days || 1;
            const dailyOnCall = onCallCost / days;
            const dailyTotal = (m.totals.total_cost || 0) / days;

            // Period label based on preset
            let periodLabel = "";
            if (preset === "day") periodLabel = "Giornaliero";
            else if (preset === "week") periodLabel = "Settimanale";
            else if (preset === "month") periodLabel = "Mensile";
            else periodLabel = "Personalizzato";

            const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const pageW = doc.internal.pageSize.getWidth();

            doc.setFillColor(10, 35, 66);
            doc.rect(0, 0, pageW, 28, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.text(`Statistiche costi — ${periodLabel}`, 14, 12);
            doc.setFontSize(11);
            doc.text(`Periodo: ${startStr} → ${endStr}  (${days} giorni)`, 14, 20);

            doc.setTextColor(0, 0, 0);
            let y = 38;

            doc.setFontSize(13);
            doc.setFont(undefined, "bold");
            doc.text("Riepilogo costi", 14, y);
            doc.setFont(undefined, "normal");
            y += 6;

            autoTable(doc, {
                startY: y,
                head: [["Voce", "Importo"]],
                body: [
                    ["Totale costo personale A CHIAMATA (netto)", fmtEur(onCallCost)],
                    ["Totale stipendi dipendenti in BUSTA PAGA", fmtEur(employeesSalary)],
                    ["Totale EXTRA BUSTA (tutti i dipendenti)", fmtEur(employeesExtra + onCallExtra)],
                    [`F24 simulato (${f24Rate}% sui netti)`, fmtEur(f24Total)],
                    ["TOTALE COSTO AZIENDA (incl. F24)", fmtEur(m.totals.total_with_f24 || 0)],
                ],
                styles: { fontSize: 10, cellPadding: 3 },
                headStyles: { fillColor: [10, 35, 66], textColor: 255 },
                columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
                theme: "striped",
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 8;

            doc.setFontSize(13);
            doc.setFont(undefined, "bold");
            doc.text("Costi giornalieri medi", 14, y);
            doc.setFont(undefined, "normal");
            y += 6;
            autoTable(doc, {
                startY: y,
                head: [["Voce", "€/giorno"]],
                body: [
                    [`Costo giornaliero PERSONALE A CHIAMATA (${days} giorni)`, fmtEur(dailyOnCall)],
                    ["Costo giornaliero COMPLESSIVO (tutti) - senza F24", fmtEur(dailyTotal)],
                    ["Costo giornaliero COMPLESSIVO + F24", fmtEur((m.totals.total_with_f24 || 0) / days)],
                ],
                styles: { fontSize: 10, cellPadding: 3 },
                headStyles: { fillColor: [225, 176, 65], textColor: 10 },
                columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
                theme: "striped",
                margin: { left: 14, right: 14 },
            });
            y = doc.lastAutoTable.finalY + 8;

            if (onCallRows.length > 0) {
                doc.setFontSize(13);
                doc.setFont(undefined, "bold");
                doc.text("Dettaglio personale A CHIAMATA", 14, y);
                doc.setFont(undefined, "normal");
                y += 6;
                autoTable(doc, {
                    startY: y,
                    head: [["Nome", "Ruolo", "Ore", "€/h", "Costo ore", "Extra busta"]],
                    body: onCallRows.map((p) => [
                        p.name,
                        p.role,
                        `${p.hours} h`,
                        fmtEur(p.hourly_rate),
                        fmtEur(p.hourly_cost),
                        fmtEur(p.extra_cost),
                    ]),
                    styles: { fontSize: 9, cellPadding: 2 },
                    headStyles: { fillColor: [10, 35, 66], textColor: 255 },
                    theme: "grid",
                    margin: { left: 14, right: 14 },
                });
                y = doc.lastAutoTable.finalY + 8;
            }

            if (empRows.length > 0) {
                if (y > 230) { doc.addPage(); y = 20; }
                doc.setFontSize(13);
                doc.setFont(undefined, "bold");
                doc.text("Dettaglio DIPENDENTI in busta paga", 14, y);
                doc.setFont(undefined, "normal");
                y += 6;
                autoTable(doc, {
                    startY: y,
                    head: [["Nome", "Ruolo", "Stipendio netto", "Extra busta", `F24 sim. (${f24Rate}%)`, "Totale + F24"]],
                    body: empRows.map((p) => [
                        p.name,
                        p.role,
                        fmtEur(p.monthly_cost_in_range || 0),
                        fmtEur(p.extra_cost),
                        fmtEur(p.simulated_f24 || 0),
                        fmtEur((p.total_cost || 0) + (p.simulated_f24 || 0)),
                    ]),
                    styles: { fontSize: 9, cellPadding: 2 },
                    headStyles: { fillColor: [10, 35, 66], textColor: 255 },
                    theme: "grid",
                    margin: { left: 14, right: 14 },
                });
            }

            const totalPages = doc.internal.pages.length - 1;
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(120, 120, 120);
                doc.text(
                    `Staff Flow 360 — Pagina ${i}/${totalPages}`,
                    pageW - 14,
                    doc.internal.pageSize.getHeight() - 8,
                    { align: "right" },
                );
            }

            doc.save(`statistiche_${startStr}_${endStr}.pdf`);
            toast.success("PDF statistiche generato");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione report");
        }
    };

    const exportShiftsPDF = async () => {
        try {
            const { data: shiftsData } = await api.get("/shifts", {
                params: { start, end },
            });
            const items = shiftsData.items || [];
            if (items.length === 0) {
                toast.error("Nessun turno nel periodo selezionato");
                return;
            }
            // Build week-day matrix: rows = employees, cols = days
            const startDate = new Date(start);
            const endDate = new Date(end);
            const dayList = [];
            for (let d = new Date(startDate); d <= endDate; d = addDays(d, 1)) {
                dayList.push(new Date(d));
            }
            // Group by employee
            const byEmp = {};
            items.forEach((s) => {
                if (!byEmp[s.employee_id])
                    byEmp[s.employee_id] = {
                        name: s.employee_name,
                        days: {},
                    };
                if (!byEmp[s.employee_id].days[s.date])
                    byEmp[s.employee_id].days[s.date] = [];
                byEmp[s.employee_id].days[s.date].push(
                    `${s.start_time}-${s.end_time}${s.location ? ` · ${s.location}` : ""}`,
                );
            });

            const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
            const dayLabels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
            doc.setFontSize(16);
            doc.text("Staff Flow 360 — Turni settimanali", 14, 14);
            doc.setFontSize(10);
            doc.text(
                `Periodo: ${start} → ${end}`,
                14,
                20,
            );

            const head = [
                [
                    "Dipendente",
                    ...dayList.map((d) => {
                        const idx = (d.getDay() + 6) % 7;
                        return `${dayLabels[idx]} ${d.getDate()}/${d.getMonth() + 1}`;
                    }),
                ],
            ];
            const body = Object.values(byEmp).map((e) => [
                e.name,
                ...dayList.map((d) => (e.days[fmt(d)] || []).join("\n") || "—"),
            ]);

            autoTable(doc, {
                head,
                body,
                startY: 25,
                styles: { fontSize: 8, cellPadding: 2, valign: "top" },
                headStyles: { fillColor: [10, 35, 66], textColor: 255 },
                columnStyles: { 0: { fontStyle: "bold", cellWidth: 35 } },
                theme: "grid",
                margin: { left: 10, right: 10 },
            });

            doc.save(`turni_${start}_${end}.pdf`);
            toast.success("PDF generato");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione PDF");
        }
    };

    return (
        <div data-testid="stats-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Analisi costi</div>
                    <h1 className="font-display text-4xl text-navy">Statistiche</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        onClick={printStatsPDF}
                        data-testid="stats-print-pdf"
                        className="bg-navy text-white hover:bg-navy/90"
                    >
                        <Printer size={16} className="mr-2" />
                        Crea PDF statistiche
                    </Button>
                    <Button
                        variant="outline"
                        onClick={exportShiftsPDF}
                        data-testid="stats-export-pdf"
                        className="border-navy text-navy"
                    >
                        <FileDown size={16} className="mr-2" />
                        PDF turni
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-sf rounded-xl p-4 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div className="col-span-2 md:col-span-1">
                        <Label className="text-xs uppercase tracking-wider text-sf-muted">Preset</Label>
                        <div className="mt-1.5 inline-flex bg-[#fafaf7] border border-sf rounded-md p-1 w-full">
                            {[
                                { k: "day", l: "Giorno" },
                                { k: "week", l: "Settimana" },
                                { k: "month", l: "Mese" },
                            ].map((p) => (
                                <button
                                    key={p.k}
                                    onClick={() => applyPreset(p.k)}
                                    className={`flex-1 px-2 py-1 text-xs rounded ${preset === p.k ? "bg-navy text-white" : "text-sf-secondary"}`}
                                    data-testid={`stats-preset-${p.k}`}
                                >
                                    {p.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted">Da</Label>
                        <Input
                            type="date"
                            value={start}
                            onChange={(e) => { setStart(e.target.value); setPreset("custom"); }}
                            data-testid="stats-start"
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted">A</Label>
                        <Input
                            type="date"
                            value={end}
                            onChange={(e) => { setEnd(e.target.value); setPreset("custom"); }}
                            data-testid="stats-end"
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted">Dipendente</Label>
                        <Select value={employeeId || "all"} onValueChange={(v) => setEmployeeId(v === "all" ? "" : v)}>
                            <SelectTrigger className="mt-1.5" data-testid="stats-filter-employee">
                                <SelectValue placeholder="Tutti" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti</SelectItem>
                                {employees.map((e) => (
                                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted">Ruolo</Label>
                        <Select value={role || "all"} onValueChange={(v) => setRole(v === "all" ? "" : v)}>
                            <SelectTrigger className="mt-1.5" data-testid="stats-filter-role">
                                <SelectValue placeholder="Tutti" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti</SelectItem>
                                {ROLES.filter(Boolean).map((r) => (
                                    <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted">Postazione</Label>
                        <Input
                            value={location}
                            placeholder="Sala, Cucina..."
                            onChange={(e) => setLocation(e.target.value)}
                            data-testid="stats-filter-location"
                            className="mt-1.5"
                        />
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Label className="text-xs uppercase tracking-wider text-sf-muted">
                        F24 simulato % (sui netti)
                    </Label>
                    <Input
                        type="number"
                        min="0"
                        max="100"
                        step="5"
                        value={f24Rate}
                        onChange={(e) => setF24Rate(parseFloat(e.target.value || 0))}
                        data-testid="stats-f24-rate"
                        className="max-w-[120px]"
                    />
                    <span className="text-xs text-sf-muted">
                        % di contributi/oneri sul netto mensile (default 55%)
                    </span>
                </div>

                <div className="mt-4 pt-4 border-t border-sf grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted mb-2 block">
                            Tipo contratto
                        </Label>
                        <div className="inline-flex bg-[#fafaf7] border border-sf rounded-md p-1 w-full">
                            {[
                                { k: "all", l: "Tutti" },
                                { k: "employee", l: "Dipendenti" },
                                { k: "on_call", l: "A chiamata" },
                            ].map((t) => (
                                <button
                                    key={t.k}
                                    onClick={() => setEmpType(t.k)}
                                    className={`flex-1 px-3 py-1.5 text-xs rounded ${empType === t.k ? "bg-navy text-white" : "text-sf-secondary"}`}
                                    data-testid={`stats-emptype-${t.k}`}
                                >
                                    {t.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider text-sf-muted mb-2 block">
                            Categorie costo da mostrare
                        </Label>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { k: "hourly", l: "Ore lavorate" },
                                { k: "salary", l: "Stipendi netti" },
                                { k: "extra", l: "Extra busta" },
                                { k: "f24", l: "F24 simulato" },
                            ].map((c) => {
                                const active = showCats[c.k];
                                return (
                                    <button
                                        key={c.k}
                                        onClick={() =>
                                            setShowCats({
                                                ...showCats,
                                                [c.k]: !active,
                                            })
                                        }
                                        className={`px-3 py-1.5 text-xs rounded-full border transition ${
                                            active
                                                ? "bg-amber-brand/20 border-amber-brand text-navy font-medium"
                                                : "bg-white border-sf text-sf-muted line-through"
                                        }`}
                                        data-testid={`stats-cat-${c.k}`}
                                    >
                                        {c.l}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <Button
                        onClick={load}
                        className="bg-navy text-white"
                        disabled={loading}
                        data-testid="stats-apply"
                    >
                        {loading ? "Calcolo…" : "Applica filtri"}
                    </Button>
                </div>
            </div>

            {/* KPI cards */}
            {data && (() => {
                const filteredEmps = data.per_employee.filter((p) => {
                    if (empType === "all") return true;
                    return p.employment_type === empType;
                });
                // Recompute totals based on filtered employees and visible categories
                const t = filteredEmps.reduce(
                    (acc, p) => ({
                        hours: acc.hours + (p.hours || 0),
                        hourly: acc.hourly + (p.hourly_cost || 0),
                        salary: acc.salary + (p.monthly_cost_in_range || 0),
                        extra: acc.extra + (p.extra_cost || 0),
                        f24: acc.f24 + (p.simulated_f24 || 0),
                        shifts: acc.shifts + (p.shifts || 0),
                    }),
                    { hours: 0, hourly: 0, salary: 0, extra: 0, f24: 0, shifts: 0 },
                );
                const visibleTotal =
                    (showCats.hourly ? t.hourly : 0) +
                    (showCats.salary ? t.salary : 0) +
                    (showCats.extra ? t.extra : 0);
                const visibleTotalWithF24 = visibleTotal + (showCats.f24 ? t.f24 : 0);
                const days = data.range.days || 1;
                return (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                        <KpiCard
                            icon={<Clock size={18} />}
                            label="Ore pianificate"
                            value={`${t.hours.toFixed(1)} h`}
                            sub={`${t.shifts} turni`}
                            testid="kpi-hours"
                        />
                        {showCats.hourly && (
                        <KpiCard
                            icon={<Euro size={18} />}
                            label="Costo ore lavorate"
                            value={fmtEur(t.hourly)}
                            sub="Σ (ore × €/h), solo on-call"
                            testid="kpi-hourly"
                        />)}
                        {showCats.extra && (
                        <KpiCard
                            icon={<TrendingUp size={18} />}
                            label="Extra busta"
                            value={fmtEur(t.extra)}
                            sub="Netto mensile fisso × mesi"
                            testid="kpi-extra"
                            highlight
                        />)}
                        {showCats.salary && (
                        <KpiCard
                            icon={<Users size={18} />}
                            label="Stipendi netti"
                            value={fmtEur(t.salary)}
                            sub={`${data.range.months} mese/i nel range`}
                            testid="kpi-monthly"
                        />)}
                        {showCats.f24 && (
                        <KpiCard
                            icon={<Euro size={18} />}
                            label={`F24 simulato (${f24Rate}%)`}
                            value={fmtEur(t.f24)}
                            sub="Solo su netti, NO extra busta"
                            testid="kpi-f24"
                        />)}
                    </div>

                    <div className="bg-navy text-white rounded-xl p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <div className="label-eyebrow text-amber-brand mb-1">Costo totale (senza F24)</div>
                            <div className="font-display text-3xl" data-testid="stats-total-cost">
                                {fmtEur(visibleTotal)}
                            </div>
                            <div className="text-xs opacity-70 mt-1">Solo categorie attive</div>
                        </div>
                        <div>
                            <div className="label-eyebrow text-amber-brand mb-1">Totale + F24 simulato</div>
                            <div className="font-display text-3xl" data-testid="stats-total-with-f24">
                                {fmtEur(visibleTotalWithF24)}
                            </div>
                            <div className="text-xs opacity-70 mt-1">Costo azienda stimato</div>
                        </div>
                        <div>
                            <div className="label-eyebrow text-amber-brand mb-1">Costo giornaliero medio</div>
                            <div className="font-display text-3xl">
                                {fmtEur(visibleTotal / days)}
                            </div>
                            <div className="text-xs opacity-70 mt-1">{days} giorni nel range</div>
                        </div>
                    </div>

                    {/* Daily breakdown - only hourly cost varies day by day */}
                    <Section title="Dettaglio giornaliero (costi variabili)">
                        <p className="text-xs text-sf-muted mb-2">
                            Mostra solo il costo orario (ore × tariffa on-call). Extra busta e stipendi sono importi fissi mensili: non variano con i giorni.
                        </p>
                        <DataTable
                            headers={["Data", "Turni", "Ore", "Costo ore lavorate"]}
                            rows={data.daily.map((d) => [
                                d.date,
                                d.shifts,
                                `${d.hours} h`,
                                fmtEur(d.hourly_cost),
                            ])}
                            testid="stats-daily-table"
                            emptyText="Nessun turno nel periodo"
                        />
                    </Section>

                    {/* Weekly breakdown */}
                    <Section title="Dettaglio settimanale (costi variabili)">
                        <DataTable
                            headers={["Settimana ISO", "Turni", "Ore", "Costo ore lavorate"]}
                            rows={data.weekly.map((w) => [
                                w.week,
                                w.shifts,
                                `${w.hours} h`,
                                fmtEur(w.hourly_cost),
                            ])}
                            testid="stats-weekly-table"
                            emptyText="Nessun turno"
                        />
                    </Section>

                    {/* Per employee */}
                    <Section title="Per dipendente">
                        <DataTable
                            headers={[
                                "Dipendente",
                                "Tipo",
                                "Ruolo",
                                "Ore",
                                "€/h",
                                ...(showCats.hourly ? ["Costo ore lavorate"] : []),
                                ...(showCats.salary ? ["Stipendio netto"] : []),
                                ...(showCats.extra ? ["Extra busta"] : []),
                                ...(showCats.f24 ? [`F24 sim. (${f24Rate}%)`] : []),
                                "Totale (no F24)",
                                ...(showCats.f24 ? ["Totale + F24"] : []),
                            ]}
                            rows={filteredEmps.map((p) => {
                                const tot =
                                    (showCats.hourly ? p.hourly_cost || 0 : 0) +
                                    (showCats.salary ? p.monthly_cost_in_range || 0 : 0) +
                                    (showCats.extra ? p.extra_cost || 0 : 0);
                                return [
                                    p.name + (p.skip_hourly_cost ? " ★" : ""),
                                    p.employment_type === "employee" ? "Dipendente" : "A chiamata",
                                    p.role,
                                    `${p.hours} h`,
                                    fmtEur(p.hourly_rate),
                                    ...(showCats.hourly ? [fmtEur(p.hourly_cost)] : []),
                                    ...(showCats.salary ? [fmtEur(p.monthly_cost_in_range || 0)] : []),
                                    ...(showCats.extra ? [fmtEur(p.extra_cost)] : []),
                                    ...(showCats.f24 ? [fmtEur(p.simulated_f24 || 0)] : []),
                                    fmtEur(tot),
                                    ...(showCats.f24 ? [fmtEur(tot + (p.simulated_f24 || 0))] : []),
                                ];
                            })}
                            testid="stats-employees-table"
                            emptyText="Nessun dipendente"
                        />
                        <div className="text-xs text-sf-muted mt-2">
                            ★ = &quot;Non calcolare ore extra&quot; attivo (costo orario = 0). F24 è calcolato SOLO sul stipendio netto, NON sull&apos;extra busta.
                        </div>
                    </Section>
                </>
                );
            })()}
        </div>
    );
}

function KpiCard({ icon, label, value, sub, testid, highlight }) {
    return (
        <div
            className={`rounded-xl p-4 border ${highlight ? "bg-amber-brand/15 border-amber-brand/40" : "bg-white border-sf"}`}
            data-testid={testid}
        >
            <div className="flex items-center gap-2 text-sf-secondary text-xs uppercase tracking-wider mb-2">
                {icon}
                {label}
            </div>
            <div className="font-display text-2xl text-navy">{value}</div>
            {sub && <div className="text-xs text-sf-muted mt-1">{sub}</div>}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="mb-6">
            <h2 className="font-display text-xl text-navy mb-3">{title}</h2>
            {children}
        </div>
    );
}

function DataTable({ headers, rows, testid, emptyText }) {
    return (
        <div className="bg-white border border-sf rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid={testid}>
                <thead className="bg-[#fafaf7]">
                    <tr>
                        {headers.map((h, i) => (
                            <th key={i} className="px-4 py-3 text-left label-eyebrow">{h}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={headers.length} className="text-center py-10 text-sf-muted">
                                {emptyText}
                            </td>
                        </tr>
                    ) : (
                        rows.map((r, i) => (
                            <tr key={i} className="border-t border-sf hover:bg-[#fafaf7]">
                                {r.map((c, j) => (
                                    <td key={j} className={`px-4 py-3 ${j === 0 ? "font-medium" : ""}`}>{c}</td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
